import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { CheckCircle, MapPin, Calendar, Mail, Phone, Home, Clock, XCircle, Printer, Download, User } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../locales/LanguageContext';

export const ConfirmationPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  const STATUS_MAP = {
    paid: { label: t('confirmation.status_paid'), color: 'bg-blue-600', textColor: 'text-blue-700', bgLight: 'bg-blue-50 border-blue-200' },
    confirmed: { label: t('confirmation.status_confirmed'), color: 'bg-emerald-600', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50 border-emerald-200' },
    cancelled: { label: t('confirmation.status_cancelled'), color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-200' },
    rejected: { label: t('confirmation.status_rejected'), color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-200' },
    pending: { label: t('confirmation.status_pending'), color: 'bg-gray-500', textColor: 'text-gray-700', bgLight: 'bg-gray-50 border-gray-200' },
  };
  
  useEffect(() => {
    const loadReservation = async () => {
      try {
        const response = await publicApi.getReservation(reservationId);
        setReservation(response.data);
      } catch (error) {
        console.error('Error loading reservation:', error);
        toast.error(t('confirmation.error_loading'));
      } finally {
        setLoading(false);
      }
    };
    loadReservation();
  }, [reservationId]);
  
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-navy-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!reservation) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-slate-600 mb-4">{t('confirmation.not_found')}</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-navy-800 text-white rounded-lg hover:bg-navy-900">
            {t('confirmation.back_home')}
          </button>
        </div>
      </div>
    );
  }
  
  const locale = language === 'en' ? 'en-US' : 'es-ES';
  const status = STATUS_MAP[reservation.status] || STATUS_MAP.pending;
  const invoiceDate = reservation.created_at
    ? new Date(reservation.created_at).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
  const pickupFormatted = new Date(reservation.pickup_at).toLocaleString(locale, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .invoice-container { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
          .invoice-page { min-height: auto !important; padding: 0 !important; background: white !important; }
        }
        @media screen { .print-only { display: none; } }
      `}</style>

      <div className="invoice-page min-h-screen bg-slate-100 py-8 px-4">
        {/* Action buttons - no print */}
        <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700
              rounded-lg font-medium transition-colors border border-slate-200 text-sm shadow-sm"
          >
            <Home className="w-4 h-4" />
            {t('confirmation.back_home')}
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy-800 hover:bg-navy-900 text-white
                rounded-lg font-semibold transition-colors text-sm shadow-sm"
            >
              <Printer className="w-4 h-4" />
              {t('confirmation.print_invoice')}
            </button>
          </div>
        </div>

        {/* Invoice */}
        <div className="invoice-container max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
          
          {/* Invoice Header */}
          <div className="px-8 pt-8 pb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="font-manrope font-bold text-2xl text-navy-950 tracking-tight">Sam's Transfer</h1>
                <p className="text-sm text-slate-500 mt-0.5">Servicio profesional de traslados</p>
              </div>
              <div className="text-right">
                <p className="font-manrope font-bold text-lg text-slate-800 uppercase tracking-wider">{t('confirmation.invoice')}</p>
                <p className="text-xs text-slate-400 mt-1 font-mono">#{reservation.reservation_id.toUpperCase()}</p>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-navy-800 via-indigo-400 to-transparent mt-5"></div>
          </div>

          {/* Meta Info Row */}
          <div className="px-8 pb-6">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-1">{t('confirmation.issue_date')}</p>
                <p className="text-sm font-medium text-slate-800">{invoiceDate}</p>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-1">{t('confirmation.reservation_id')}</p>
                <p className="text-sm font-mono font-medium text-slate-800">{reservation.reservation_id.toUpperCase()}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-1">{t('confirmation.status')}</p>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full border ${status.bgLight} ${status.textColor}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${status.color}`}></span>
                  {status.label}
                </span>
              </div>
            </div>
          </div>

          {/* Status Banner */}
          {reservation.status === 'paid' && (
            <div className="mx-8 mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 font-medium">{t('confirmation.payment_received')}</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {t('confirmation.pending_approval')} <strong>{reservation.customer.email}</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
          {reservation.status === 'confirmed' && (
            <div className="mx-8 mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 font-medium">{t('confirmation.reservation_confirmed')}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{t('confirmation.reminder_24h')}</p>
                </div>
              </div>
            </div>
          )}
          {(reservation.status === 'cancelled' || reservation.status === 'rejected') && (
            <div className="mx-8 mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-800 font-medium">
                    {reservation.status === 'rejected' ? t('confirmation.reservation_rejected') : t('confirmation.reservation_cancelled_text')}
                  </p>
                  {reservation.status_reason && (
                    <p className="text-sm text-red-600 mt-0.5">{t('confirmation.reason')}: {reservation.status_reason}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Two-column: Client Info + Trip Details */}
          <div className="px-8 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client */}
            <div className="bg-slate-50 rounded-xl p-5">
              <h3 className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-3">{t('confirmation.client_data')}</h3>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-800">{reservation.customer.full_name}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{reservation.customer.email}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{reservation.customer.phone}</span>
                </div>
              </div>
            </div>
            {/* Trip */}
            <div className="bg-slate-50 rounded-xl p-5">
              <h3 className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-3">{t('confirmation.service_details')}</h3>
              <div className="space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <MapPin className="w-4 h-4 text-navy-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{reservation.from_label}</p>
                    <p className="text-xs text-slate-400">{t('confirmation.towards')}</p>
                    <p className="text-sm font-medium text-slate-800">{reservation.to_label}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 text-navy-600" />
                  <span className="text-sm text-slate-700">{pickupFormatted}</span>
                </div>
                {reservation.is_roundtrip && reservation.return_at && (
                  <div className="flex items-start gap-2.5">
                    <Calendar className="w-4 h-4 text-navy-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-400">{t('confirmation.return_label')}</p>
                      <p className="text-sm font-medium text-slate-800">
                        {new Date(reservation.return_at).toLocaleString(locale, {
                          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Service line items table */}
          <div className="px-8 pb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">{t('confirmation.concept')}</th>
                  <th className="text-center py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">{t('confirmation.details')}</th>
                  <th className="text-right py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">{t('confirmation.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Main service - base price */}
                <tr className="border-b border-slate-100">
                  <td className="py-3.5">
                    <p className="text-sm font-medium text-slate-800">{t('confirmation.transfer_service')}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {reservation.from_label} &rarr; {reservation.to_label}
                      {reservation.is_roundtrip && ` (${t('confirmation.roundtrip_included')})`}
                    </p>
                  </td>
                  <td className="py-3.5 text-center">
                    <span className="text-sm text-slate-600">
                      {reservation.passengers} {reservation.passengers === 1 ? t('confirmation.passenger_singular') : t('confirmation.passengers_plural')}
                    </span>
                  </td>
                  <td className="py-3.5 text-right">
                    <span className="text-sm font-semibold text-slate-800">
                      ${Number(reservation.base_price || reservation.total).toFixed(2)}
                    </span>
                  </td>
                </tr>

                {/* Return date info */}
                {reservation.is_roundtrip && reservation.return_at && (
                  <tr className="border-b border-slate-100">
                    <td className="py-3.5" colSpan={2}>
                      <p className="text-xs text-slate-500">
                        {t('confirmation.return_label')}: {new Date(reservation.return_at).toLocaleString(locale, {
                          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </td>
                    <td className="py-3.5"></td>
                  </tr>
                )}

                {/* Pickup exact address */}
                {(reservation.pickup_exact_address || reservation.pickup_address) && (
                  <tr className="border-b border-slate-100">
                    <td className="py-3.5" colSpan={2}>
                      <p className="text-sm text-slate-600">{t('confirmation.pickup_exact')}: <span className="font-medium text-slate-800">{reservation.pickup_exact_address || reservation.pickup_address}</span></p>
                    </td>
                    <td className="py-3.5"></td>
                  </tr>
                )}

                {/* Dropoff exact address */}
                {reservation.dropoff_exact_address && (
                  <tr className="border-b border-slate-100">
                    <td className="py-3.5" colSpan={2}>
                      <p className="text-sm text-slate-600">{t('confirmation.dropoff_exact')}: <span className="font-medium text-slate-800">{reservation.dropoff_exact_address}</span></p>
                    </td>
                    <td className="py-3.5"></td>
                  </tr>
                )}

                {/* Addons with prices */}
                {reservation.addons && reservation.addons.length > 0 && reservation.addons.map((addon, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-3.5">
                      <p className="text-sm text-slate-700">{addon.name || addon.addon_id}</p>
                    </td>
                    <td className="py-3.5 text-center text-sm text-slate-600">x{addon.quantity}</td>
                    <td className="py-3.5 text-right text-sm text-slate-600">
                      {addon.price
                        ? `$${(addon.type === 'PER_PERSON'
                            ? addon.price * addon.quantity * (reservation.passengers || 1)
                            : addon.price * addon.quantity).toFixed(2)}`
                        : '--'}
                    </td>
                  </tr>
                ))}

                {/* Notes */}
                {reservation.notes && (
                  <tr className="border-b border-slate-100">
                    <td className="py-3.5" colSpan={3}>
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">{t('confirmation.notes')}</p>
                      <p className="text-sm text-slate-600">{reservation.notes}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="px-8 pb-8">
            <div className="bg-indigo-50 rounded-xl p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-navy-800 font-medium">{t('confirmation.total')}</p>
                <p className="text-[11px] text-navy-600 mt-0.5">{t('confirmation.currency_label')}</p>
              </div>
              <p className="text-3xl font-bold text-navy-900 tracking-tight">
                ${Number(reservation.total).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400">{t('confirmation.footer_company')}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t('confirmation.footer_location')}</p>
              </div>
              <p className="text-xs text-slate-300 font-mono">{reservation.reservation_id}</p>
            </div>
          </div>
        </div>

        {/* Bottom actions - no print */}
        <div className="no-print max-w-3xl mx-auto mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-slate-50 text-slate-700 
              rounded-lg font-medium transition-colors border border-slate-200 text-sm"
          >
            <Printer className="w-4 h-4" />
            {t('confirmation.print')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy-800 hover:bg-navy-900 text-white
              rounded-lg font-semibold transition-colors text-sm"
            data-testid="back-to-home-btn"
          >
            <Home className="w-4 h-4" />
            {t('confirmation.back_home')}
          </button>
        </div>
      </div>
    </>
  );
};
